// Labeled select input — mirrors FormField API. DESIGN-SYSTEM.md §Form components.

import { useId, type JSX } from "react";
import type { ApiFieldName } from "../lib/visit-report-api-fields.js";

interface Option { value: string; label: string; }

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  /** Optional explicit id; falls back to a useId() value so repeated labels
   *  on the same page don't collide (a11y / HTML validity). */
  id?: string;
  required?: boolean;
  error?: string;
  /** Q9 S7 — see FormField.apiFieldName.
   *  TASK-48 S2 — typed via ApiFieldName (keyof SubmitInput + nested-path
   *  template literals). */
  apiFieldName?: ApiFieldName;
}

export function SelectField({ label, value, onChange, options, id, required, error, apiFieldName }: SelectFieldProps): JSX.Element {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <div>
      <label htmlFor={fieldId} className="block text-sm font-medium text-slate-700 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <select
        id={fieldId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        aria-invalid={!!error}
        data-api-field={apiFieldName}
        className={`w-full rounded-lg border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 ${error ? "border-red-400 focus:ring-red-400 bg-red-50" : "border-slate-300 focus:ring-navy-500"}`}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
